import type { ReactNode } from "react";
import { Modal } from "antd";
import { CalendarOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { SeatableReservation } from "../../../../../api/reservations";
import { useViewport } from "../../../../../hooks/useViewport";
import { fmtEuro } from "../../../../../utils/currency";
import { distinctTablesOf } from "../../../../reservations/logic/seatBills";
import { GhostButton, ModalFooter, ModalHeader, PrimaryButton, posModalStyles } from "../../modalPrimitives";
import type { OrderingSeatBills } from "./orderingSeatBills";
import { SeatBillsSection } from "./SeatBillsSection";
import { SeatOptionRow } from "./SeatOptionRow";
import { seatOptionKey, targetChoicesOf, type SeatOption } from "./seatOptions";
import { SeatOutcome, type SeatOutcomeRow } from "./SeatOutcome";
import { SeatReservationTicket } from "./SeatReservationTicket";
import { useSeatChoiceText } from "./useSeatChoiceText";

/**
 * „Настани Иванови?" — ВИНАГИ след „Настани", над списъка. Подредбата е на
 * CloseTableModal: кремав хедър и футър, вляво — резервацията като билет
 * (SeatReservationTicket), вдясно — изборът:
 * - „Как да се водят сметките" — само при няколко маси (SeatBillsSection);
 * - „Към коя сметка" — при поне две възможности (`targetChoicesOf`), нищо не е
 *   избрано предварително и при „Отделна…" (изборът решава и на кого са
 *   новите маси);
 * - „Какво ще стане" — винаги: масите → нова маса или отворената сметка.
 * Изключеният „Настани" казва защо. Клавишите (↑/↓ — „Към коя сметка",
 * ←/→ — сметките, Enter, Esc) са на родителя (useSeatModalKeys). На телефон
 * резервацията е горе, изборът — под нея.
 */
export const SeatChoiceModal = ({
  reservation,
  options,
  target,
  bills,
  separateBills,
  busy,
  onSelect,
  onSeparateChange,
  onBack,
  onConfirm,
}: {
  reservation: SeatableReservation | null;
  options: SeatOption[];
  // Главната сметка — изборът на сервитьора (null — още не е избрана).
  target: SeatOption | null;
  // null — резервация с една маса (без избор на сметките).
  bills: OrderingSeatBills | null;
  separateBills: boolean;
  busy: boolean;
  onSelect: (key: string) => void;
  onSeparateChange: (separateBills: boolean) => void;
  onBack: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();
  const text = useSeatChoiceText();
  const { isNarrow, isDesktop } = useViewport();
  const targetKey = target ? seatOptionKey(target) : null;
  const choices = targetChoicesOf(options);
  // Защо „Настани" е изключен: още няма избрана сметка или „Отделна…" е спряна.
  const separateBlock = separateBills && bills?.separateHint ? bills.separateHint : null;
  const separateBlockText = separateBlock ? t(separateBlock.key, separateBlock.params) : null;
  const needsTarget = target === null;
  const confirmLabel = separateBlockText ? (
    <span className="whitespace-normal text-[13px] leading-[1.25]">{separateBlockText}</span>
  ) : needsTarget ? (
    t("seatReservation.choice.pickTable")
  ) : (
    t("seatReservation.seat")
  );

  // Новите маси при „Отделна…" са на сервитьора на избраната сметка (своя
  // или нова главна — на мен): никога една маса негова, друга моя.
  const newTablesOwner =
    target && target.kind !== "new"
      ? target.bill.waiterName ?? t("reservations.seatPicker.unknownWaiter")
      : null;
  const outcomeRows: SeatOutcomeRow[] =
    separateBills && bills
      ? bills.separate.map((entry) => ({
          tables: [entry.tableName],
          option: entry.bill === null ? { kind: "new", table: entry.tableName } : { kind: "other", bill: entry.bill },
          owner: newTablesOwner,
        }))
      : target && reservation
        ? [{ tables: distinctTablesOf(reservation.tableNames), option: target }]
        : [];
  const outcomeNote = separateBlockText ?? (needsTarget ? t("reservations.seatBills.singlePick") : null);

  return (
    <Modal
      open={reservation !== null}
      onCancel={onBack}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      zIndex={1100}
      width={isNarrow ? "100%" : 760}
      styles={posModalStyles}
      // `!` — максималната ширина на antd (cssinjs) е извън Tailwind layer-ите.
      className={isNarrow ? "!max-w-[calc(100vw-16px)]" : "!max-w-[calc(100vw-32px)]"}
    >
      {reservation && (
        <div
          className={`flex flex-col bg-pos-card text-pos-ink ${
            isNarrow ? "max-h-[calc(100dvh-16px)]" : "max-h-[min(760px,90vh)]"
          }`}
        >
          <div className="shrink-0">
            <ModalHeader
              icon={<CalendarOutlined />}
              title={t("seatReservation.choice.title", { guest: reservation.guestName })}
              onClose={onBack}
              isNarrow={isNarrow}
              tone="panel"
            />
          </div>

          {/* Висок избор (няколко сметки) се превърта, хедърът и бутоните остават. */}
          <div className={`min-h-0 flex-auto overflow-y-auto overscroll-contain ${isNarrow ? "" : "flex items-stretch"}`}>
            {/* Вляво — каква е резервацията; на телефон — над избора. */}
            <div
              className={
                isNarrow
                  ? "border-b border-pos-line2 bg-pos-canvas px-4 py-3"
                  : "w-[38%] shrink-0 bg-pos-canvas px-5 py-5"
              }
            >
              <SeatReservationTicket reservation={reservation} compact={isNarrow} />
            </div>
            <div
              className={
                isNarrow
                  ? "flex flex-col gap-4 px-4 py-3.5"
                  : "flex min-w-0 flex-1 flex-col gap-5 border-l border-pos-line2 bg-pos-marked px-5 py-5"
              }
            >
              {bills && (
                <Section label={t("seatReservation.bills.label")}>
                  <SeatBillsSection
                    bills={bills}
                    separateBills={separateBills}
                    disabled={busy}
                    onChange={onSeparateChange}
                  />
                </Section>
              )}
              {choices.length > 0 && (
                <Section label={t("seatReservation.choice.pickBill")}>
                  <div role="radiogroup" aria-label={t("seatReservation.choice.pickBill")} className="flex flex-col gap-2">
                    {choices.map((option) => {
                      const key = seatOptionKey(option);
                      const card = text.target(option);
                      return (
                        <SeatOptionRow
                          key={key}
                          title={`${card.title} ${card.waiter}`}
                          meta={card.label}
                          amount={option.kind === "new" ? undefined : fmtEuro(option.bill.totalAmount)}
                          checked={key === targetKey}
                          disabled={busy}
                          onClick={() => onSelect(key)}
                        />
                      );
                    })}
                  </div>
                </Section>
              )}
              <Section label={t("seatReservation.choice.outcome")}>
                <SeatOutcome rows={outcomeRows} note={outcomeNote} />
              </Section>
            </div>
          </div>

          <div className="shrink-0">
            <ModalFooter isNarrow={isNarrow} tone="panel" narrowFirst>
              {/* Масив, не фрагмент: ModalFooter брои децата си през Children.toArray. */}
              {[
                <GhostButton
                  key="back"
                  label={t("common.back")}
                  onClick={onBack}
                  shortcut={isDesktop ? "Esc" : undefined}
                />,
                <PrimaryButton
                  key="seat"
                  label={confirmLabel}
                  icon={separateBlockText ? undefined : <CheckOutlined />}
                  onClick={onConfirm}
                  disabled={needsTarget || separateBlockText !== null}
                  loading={busy}
                  shortcut={isDesktop ? "⏎" : undefined}
                />,
              ]}
            </ModalFooter>
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Заглавие на част от избора — микро-етикетът на CloseTableModal. */
const Section = ({ label, children }: { label: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <h3 className="m-0 text-[10.5px] font-bold uppercase tracking-[1.2px] text-pos-muted">{label}</h3>
    {children}
  </section>
);
